import classnames from 'classnames';

import type {LeadFitScore} from 'nutshell-graphql-types';
import type {LeadResearchSummaryFragment_leadFitReasons} from 'nutshell-graphql-types';

import {getLeadFitVariant, isLeadFitUnknown} from '../../helpers';

import {LeadFitGauge} from '../lead-fit-gauge/lead-fit-gauge';
import {LeadFitReasonItem} from '../lead-fit-reason-item';
import {ResearchCta} from '../research-cta/research-cta';

import styles from './lead-fit.module.css';

type Props = {
    score: LeadFitScore;
    label: string;
    reasons: LeadResearchSummaryFragment_leadFitReasons[];
    isAdmin: boolean;
    onSettingsClick: () => void;
    hasConfiguredSettings: boolean;
};

export function LeadFit(props: Props) {
    const variant = getLeadFitVariant(props.score);
    const isUnknown = isLeadFitUnknown(props.score);

    return (
        <div className='mb-16'>
            <div
                className={classnames('flex flex-dir-col mb-12', styles['lead-fit-card'], {
                    [styles['lead-fit-card--ideal']]: variant === 'ideal',
                    [styles['lead-fit-card--possible']]: variant === 'possible',
                    [styles['lead-fit-card--unlikely']]: variant === 'unlikely',
                    [styles['lead-fit-card--unknown']]: variant === 'unknown',
                })}
            >
                <div className='flex justify-sb align-center'>
                    <div>
                        <div className={styles['lead-fit-label']}>Lead fit</div>
                        <div
                            className={classnames(styles['lead-fit-value'], {
                                [styles['lead-fit-value--ideal']]: variant === 'ideal',
                                [styles['lead-fit-value--possible']]: variant === 'possible',
                                [styles['lead-fit-value--unlikely']]: variant === 'unlikely',
                                [styles['lead-fit-value--unknown']]: variant === 'unknown',
                            })}
                        >
                            {props.label}
                        </div>
                    </div>
                    <div className={styles['gauge-container']}>
                        <LeadFitGauge score={props.score} />
                    </div>
                </div>
                {isUnknown && !props.hasConfiguredSettings && (
                    <div className='mt-16'>
                        <ResearchCta
                            isAdmin={props.isAdmin}
                            onSettingsClick={props.onSettingsClick}
                        />
                    </div>
                )}
            </div>
            {!isUnknown && props.reasons.length > 0 && (
                <ul className={styles['lead-fit-reasons']}>
                    {props.reasons.map((reason) => (
                        <LeadFitReasonItem
                            key={reason.text}
                            text={reason.text}
                            sentiment={reason.sentiment}
                            body={reason.body}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
